/**
 * Ritual schools — shakhas, grihya sutras and regional paddhatis (content 08 §4.2, data 08 §3.13).
 *
 * A second axis beside sampradaya, which pandits claim on their profile. The schools form a tree,
 * a sutra under its Veda, and the parent picker offers only rows the server would accept: never
 * the school itself or one of its descendants. The server checks again, because it is the one
 * that sees rows written by seeds.
 *
 * ⛔ **No delete.** A pandit's claim holds its school (`RESTRICT`), so a school is switched off
 * instead: existing claims keep reading it, and no new claim can pick it. The pandit count on each
 * row says how many profiles a switch-off would leave pointing at a retired school.
 *
 * ⛔ **Every refusal is the server's, shown verbatim**: the content lint names the rule and quotes
 * the span, a taken slug names itself, and a looping parent says so.
 */
import { useState } from 'react';
import { Card, Icon, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { Consequences, FilterBar, ScopeAction, REFUSED } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useCreateRitualSchool,
  useRitualSchools,
  useUpdateRitualSchool,
  type RitualSchoolKind,
  type RitualSchoolRow,
} from '../lib/api/hooks/ritualSchools';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';

export const KINDS: readonly RitualSchoolKind[] = ['veda_shakha', 'grihya_sutra', 'regional_paddhati'];

export const KIND_LABEL: Readonly<Record<RitualSchoolKind, string>> = {
  veda_shakha: 'Veda shakha',
  grihya_sutra: 'Grihya sutra',
  regional_paddhati: 'Regional paddhati',
};

const NO_PARENT = '— none —';

/** Ids of `id` and everything under it: the rows that can never be its parent. */
export function subtree(rows: readonly RitualSchoolRow[], id: number): Set<number> {
  const out = new Set<number>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const r of rows) {
      if (r.parent_id !== null && r.parent_id !== undefined && out.has(r.parent_id) && !out.has(r.id)) {
        out.add(r.id);
        grew = true;
      }
    }
  }
  return out;
}

/** The parent picker's options as "slug" strings, with the forbidden subtree left out. A
    switched-off school is offered only while it is already the parent — the live picker offered
    four retired E2E fixture schools as somewhere to hang a new one. */
export function parentOptions(
  rows: readonly RitualSchoolRow[],
  self: number | null,
  current: number | null = null,
): string[] {
  const banned = self === null ? new Set<number>() : subtree(rows, self);
  return [
    NO_PARENT,
    ...rows.filter((r) => !banned.has(r.id) && (r.is_active || r.id === current)).map((r) => r.slug),
  ];
}

function SchoolForm({
  row,
  rows,
  onDone,
}: {
  row: RitualSchoolRow | null;
  rows: readonly RitualSchoolRow[];
  onDone: () => void;
}) {
  const { scope, toast } = useConsole();
  const create = useCreateRitualSchool();
  const update = useUpdateRitualSchool();
  const mutation = row ? update : create;
  const [slug, setSlug] = useState(row?.slug ?? '');
  const [kind, setKind] = useState<RitualSchoolKind>((row?.school_kind as RitualSchoolKind) ?? 'veda_shakha');
  const [nameEn, setNameEn] = useState(row?.name_en ?? '');
  const [nameHi, setNameHi] = useState(row?.name_hi ?? '');
  const [nameSa, setNameSa] = useState(row?.name_sa ?? '');
  const [parent, setParent] = useState(rows.find((r) => r.id === row?.parent_id)?.slug ?? NO_PARENT);
  const [region, setRegion] = useState(row?.region_note ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [order, setOrder] = useState(String(row?.sort_order ?? 0));

  const orderValid = /^\d{1,5}$/.test(order);
  const fields = {
    school_kind: kind,
    name_en: nameEn.trim(),
    name_hi: nameHi.trim() || null,
    name_sa: nameSa.trim() || null,
    parent_id: rows.find((r) => r.slug === parent)?.id ?? null,
    region_note: region.trim() || null,
    description: description.trim() || null,
    sort_order: Number(order),
  };
  const done = (message: string) => {
    toast({ tone: 'success', message });
    onDone();
  };
  const save = () =>
    row
      ? update.mutate({ id: row.id, patch: fields }, { onSuccess: () => done(`${row.slug} saved`) })
      : create.mutate(
          { ...fields, slug: slug.trim(), is_active: true },
          { onSuccess: () => done(`${slug.trim()} added`) },
        );
  const toggle = (target: RitualSchoolRow) =>
    update.mutate(
      { id: target.id, patch: { is_active: !target.is_active } },
      {
        onSuccess: () => {
          toast({
            tone: target.is_active ? 'info' : 'success',
            message: `${target.slug} ${target.is_active ? 'switched off' : 'switched on'}`,
          });
          onDone();
        },
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <strong>{row ? row.slug : 'New ritual school'}</strong>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px', gap: 'var(--sp-md)' }}>
          {row ? (
            <span style={{ alignSelf: 'end', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
              The slug is fixed once created — seeds and imports cite it.
            </span>
          ) : (
            <Input density="admin" label="Slug" value={slug} onChange={setSlug} placeholder="paraskara-grihya-sutra" />
          )}
          <Select
            density="admin"
            label="Kind"
            value={KIND_LABEL[kind]}
            onChange={(v) => setKind(KINDS.find((k) => KIND_LABEL[k] === v)!)}
            options={KINDS.map((k) => KIND_LABEL[k])}
          />
        </div>
        <Input density="admin" label="Name (English)" value={nameEn} onChange={setNameEn} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-md)' }}>
          <Input density="admin" label="Name (Hindi)" value={nameHi} onChange={setNameHi} />
          <Input density="admin" label="Name (Sanskrit)" value={nameSa} onChange={setNameSa} />
        </div>
        <Select
          density="admin"
          label="Parent"
          value={parent}
          onChange={setParent}
          options={parentOptions(rows, row?.id ?? null, row?.parent_id ?? null)}
        />
        <Input density="admin" label="Region" value={region} onChange={setRegion} placeholder="North India, Mithila" />
        <Input
          density="admin"
          label="Description"
          value={description}
          onChange={setDescription}
          multiline
          helper="Descriptive, never comparative: what the school is, not that it is better."
        />
        <Input
          density="admin"
          label="Order"
          value={order}
          onChange={setOrder}
          inputMode="numeric"
          error={orderValid ? undefined : 'A whole number, 0 to 65535.'}
        />
        {mutation.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(mutation.error)]} />}
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!nameEn.trim() || (!row && !slug.trim()) || !orderValid || mutation.isPending}
            onClick={save}
          >
            {row ? 'Save' : 'Add school'}
          </ScopeAction>
          {row && (
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant={row.is_active ? 'destructive' : 'secondary'}
              disabled={mutation.isPending}
              onClick={() => toggle(row)}
            >
              {row.is_active ? 'Switch off' : 'Switch on'}
            </ScopeAction>
          )}
          <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={onDone}>
            {row ? 'Close' : 'Cancel'}
          </ScopeAction>
        </div>
        {row && row.is_active && row.pandit_count > 0 && (
          <Consequences
            tone="warn"
            items={[
              `${row.pandit_count} pandit profile${row.pandit_count === 1 ? '' : 's'} claim this school. Switching it off keeps those claims readable but stops anyone new choosing it.`,
            ]}
          />
        )}
      </div>
    </Card>
  );
}

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS: readonly string[] = ['all', ...KINDS, 'off'];

export function RitualSchools() {
  const { scope } = useConsole();
  const [q, setQ] = useUrlSearch('schools');
  const [tab, setTab] = useUrlTab('schools', TAB_KEYS, 'all');
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  /* Read everything, switched-off rows included: the parent picker and the tree need the whole
     set, and the tab filters it here. The table is reference data, tens of rows, not thousands. */
  const list = useRitualSchools({ include_inactive: true });
  const all = list.data?.items ?? [];
  const needle = q.trim().toLowerCase();
  const rows = all.filter(
    (r) =>
      (tab === 'all' ? r.is_active : tab === 'off' ? !r.is_active : r.school_kind === tab && r.is_active) &&
      (!needle || `${r.slug} ${r.name_en} ${r.name_hi ?? ''}`.toLowerCase().includes(needle)),
  );
  const open = all.find((r) => r.id === editing) ?? null;
  const parentSlug = (id: number | null | undefined) => all.find((r) => r.id === id)?.slug;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-sunk)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          color: 'var(--c-ink2)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name="book-open" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          The shakhas, sutras and paddhatis a pandit can say they follow. Schools are switched off,
          never deleted, because a pandit&rsquo;s claim holds its school.
        </span>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          icon="plus"
          onClick={() => {
            setEditing(null);
            setAdding(true);
          }}
        >
          New school
        </ScopeAction>
      </div>

      {list.isPending && <LoadingState message="Reading the ritual schools…" />}
      {list.isError && <ErrorState title="Could not read the ritual schools" cause={errorMessage(list.error)} />}

      {adding && <SchoolForm row={null} rows={all} onDone={() => setAdding(false)} />}
      {open && <SchoolForm key={open.id} row={open} rows={all} onDone={() => setEditing(null)} />}

      {list.isSuccess && all.length === 0 && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="book-open"
            title="No ritual schools yet"
            body="The seed adds the standard set. An empty table means it has not run, not that pandits have no schools to choose."
          />
        </Card>
      )}

      {list.isSuccess && all.length > 0 && (
        <>
          <FilterBar
            q={q}
            onQ={setQ}
            placeholder="Slug or name — press /"
            tab={tab}
            onTab={setTab}
            tabs={[
              { key: 'all', label: 'Active' },
              ...KINDS.map((k) => ({ key: k, label: KIND_LABEL[k] })),
              { key: 'off', label: 'Switched off' },
            ]}
          />
          <DataTable
            density="comfortable"
            onRowClick={(row) => {
              setAdding(false);
              setEditing(Number(row.id));
            }}
            footer={<span>{rows.length} of {all.length} schools</span>}
            columns={[
              { key: 'slug', label: 'Slug', mono: true },
              { key: 'name', label: 'Name' },
              { key: 'kind', label: 'Kind' },
              { key: 'parent', label: 'Under', mono: true },
              { key: 'pandits', label: 'Pandits', align: 'right' },
              { key: 'status', label: 'Status' },
            ]}
            rows={rows.map((r) => ({
              id: r.id,
              slug: r.slug,
              name: r.name_hi ? `${r.name_en} · ${r.name_hi}` : r.name_en,
              kind: KIND_LABEL[r.school_kind as RitualSchoolKind],
              parent: parentSlug(r.parent_id) ?? <span style={{ color: 'var(--c-ink3)' }}>—</span>,
              pandits: r.pandit_count,
              status: <StatusChip tone={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'Offered' : 'Off'} />,
            }))}
          />
        </>
      )}
    </>
  );
}
